import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { t } from '@/i18n'
import {
  attachmentFileUrl,
  commitStatementFromExtraction,
  confirmExtraction,
  getAccounts,
  getExtractionJob,
  getExtractionJobs,
  rejectExtraction,
} from '@/lib/api'
import { formatDecimalInput, parseAmount } from '@/lib/format'

const STATEMENT_FIELDS: { key: string; numeric?: boolean }[] = [
  { key: 'booked_at' },
  { key: 'amount', numeric: true },
  { key: 'direction' },
  { key: 'currency' },
  { key: 'payer_name' },
  { key: 'payer_account' },
  { key: 'description' },
  { key: 'reference' },
]

const FIELD_DEFS: { key: string; numeric?: boolean; required?: boolean }[] = [
  { key: 'exchange' },
  { key: 'order_id' },
  { key: 'occurred_at' },
  { key: 'side' },
  { key: 'asset' },
  { key: 'quantity', numeric: true, required: true },
  { key: 'price', numeric: true, required: true },
  { key: 'fiat' },
  { key: 'total_fiat', numeric: true },
  { key: 'bank' },
  { key: 'payment_method' },
  { key: 'counterparty' },
  { key: 'order_status' },
  { key: 'fee', numeric: true },
]

const STATUS_KEY: Record<string, string> = {
  queued: 'st.queued',
  running: 'st.running',
  review: 'st.review',
  committed: 'st.committed',
  rejected: 'st.rejected',
  failed: 'st.failed',
}

export function Review() {
  const { jobId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [values, setValues] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [accountId, setAccountId] = useState('')

  const job = useQuery({
    queryKey: ['extraction', 'job', jobId],
    queryFn: () => getExtractionJob(jobId!),
    enabled: Boolean(jobId),
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === 'queued' || status === 'running' ? 1500 : false
    },
  })
  const queue = useQuery({
    queryKey: ['extraction', 'jobs', 'review'],
    queryFn: () => getExtractionJobs('review'),
  })
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: getAccounts })
  const bankAccounts = (accounts.data ?? []).filter((item) => item.kind === 'bank')
  const isStatement = job.data?.detected_screen_type === 'bank_statement'

  useEffect(() => {
    const data = job.data
    if (!data) return
    const next: Record<string, string> = {}
    if (data.detected_exchange) next.exchange = data.detected_exchange
    for (const field of data.fields) {
      const raw = field.value_final || field.value_vlm || field.value_ocr || ''
      const numeric = FIELD_DEFS.some((def) => def.key === field.field_name && def.numeric)
        || STATEMENT_FIELDS.some((def) => def.key === field.field_name && def.numeric)
      next[field.field_name] = numeric ? formatDecimalInput(raw) : raw
    }
    setValues(next)
    setError(null)
  }, [job.data])

  const fieldMeta = useMemo(() => {
    const map = new Map((job.data?.fields ?? []).map((item) => [item.field_name, item]))
    return map
  }, [job.data])

  const confirm = useMutation({
    mutationFn: async () => {
      if (isStatement) {
        if (!accountId) throw new Error(t('rev.needBank'))
        await commitStatementFromExtraction(jobId!, { account_id: accountId, rows: [values] })
        return
      }
      const qty = parseAmount(values.quantity ?? '')
      const unit = parseAmount(values.price ?? '')
      if (!qty || !unit) {
        throw new Error(t('rev.needNums'))
      }
      const fields = {
        ...values,
        quantity: qty,
        price: unit,
        total_fiat: parseAmount(values.total_fiat ?? '') || values.total_fiat || '',
      }
      await confirmExtraction(jobId!, { row_index: 0, fields })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['extraction'] })
      await queryClient.invalidateQueries({ queryKey: ['orders'] })
      await queryClient.invalidateQueries({ queryKey: ['transactions'] })
      const remaining = (queue.data ?? []).filter((item) => item.id !== jobId)
      navigate(remaining[0] ? `/screenshots/${remaining[0].id}` : isStatement ? '/transactions' : '/screenshots')
    },
    onError: (err) => setError(err instanceof Error ? err.message : t('rev.confirmFail')),
  })
  const reject = useMutation({
    mutationFn: () => rejectExtraction(jobId!),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['extraction'] })
      const remaining = (queue.data ?? []).filter((item) => item.id !== jobId)
      navigate(remaining[0] ? `/screenshots/${remaining[0].id}` : '/screenshots')
    },
    onError: (err) => setError(err instanceof Error ? err.message : t('rev.rejectFail')),
  })

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        if (job.data?.status === 'review') confirm.mutate()
      }
      if (event.key === 'Escape') {
        event.preventDefault()
        if (job.data?.status === 'review') reject.mutate()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [confirm, reject, job.data?.status])

  if (!jobId) return null
  if (job.isLoading) {
    return <p className="px-4 py-5 md:px-8 md:py-8 text-sm text-slate-500">{t('common.loading')}</p>
  }
  if (job.isError || !job.data) {
    return (
      <p className="px-4 py-5 md:px-8 md:py-8 text-sm text-loss">
        {job.error instanceof Error ? job.error.message : t('rev.notFound')}
      </p>
    )
  }

  const current = job.data
  const boxes = current.fields.filter((item) => item.bbox && item.bbox.w && item.bbox.h)
  const statusKey = STATUS_KEY[current.status]

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between border-b border-surface-border px-6 py-3">
        <div>
          <Link to="/screenshots" className="text-xs text-slate-500 hover:text-slate-300">
            ← {t('rev.queue')}
          </Link>
          <h1 className="text-lg font-semibold text-slate-100">
            {current.original_filename || t('rev.title')}
          </h1>
          <p className="text-xs text-slate-500">
            {t('rev.keys')}
            {current.private ? t('rev.private') : ''}
            {current.provider ? ` · ${current.provider}` : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="rounded-md border border-surface-border px-3 py-1.5 text-sm text-slate-300"
            onClick={() => reject.mutate()}
            disabled={current.status !== 'review' || reject.isPending}
          >
            {t('rev.reject')}
          </button>
          <button
            type="button"
            className="rounded-md bg-slate-100 px-3 py-1.5 text-sm text-slate-900"
            onClick={() => confirm.mutate()}
            disabled={current.status !== 'review' || confirm.isPending}
          >
            {t('rev.confirm')}
          </button>
        </div>
      </div>

      {current.status !== 'review' && (
        <p className="border-b border-surface-border px-6 py-2 text-sm text-slate-400">
          {t('rev.status')} {statusKey ? t(statusKey) : current.status}
          {current.status === 'queued' || current.status === 'running' ? t('rev.wait') : ''}
        </p>
      )}
      {current.error && <p className="border-b border-surface-border px-6 py-2 text-sm text-pending">{current.error}</p>}
      {error && <p className="border-b border-surface-border px-6 py-2 text-sm text-loss">{error}</p>}
      {!isStatement && current.status === 'review' && (
        <p className="border-b border-surface-border px-6 py-2 text-sm text-slate-400">
          {(values.quantity && values.price)
            ? t('rev.okHint')
            : t('rev.emptyHint')}
        </p>
      )}
      {current.validation_flags.length > 0 && (
        <ul className="border-b border-surface-border px-6 py-2 text-xs text-pending">
          {current.validation_flags.map((flag, index) => (
            <li key={`${flag.code}-${index}`}>{flag.message}</li>
          ))}
        </ul>
      )}

      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-2">
        <div className="relative overflow-auto border-b border-surface-border bg-black/40 lg:border-b-0 lg:border-r">
          <div className="relative inline-block min-w-full">
            <img
              src={attachmentFileUrl(current.attachment_id)}
              alt={t('rev.alt')}
              className="max-h-[calc(100vh-12rem)] w-full object-contain"
            />
            {boxes.map((field) => {
              const box = field.bbox!
              return (
                <span
                  key={`${field.row_index}-${field.field_name}`}
                  className="pointer-events-none absolute border border-pending/70 bg-pending/10"
                  style={{
                    left: box.x,
                    top: box.y,
                    width: box.w,
                    height: box.h,
                  }}
                  title={field.field_name}
                />
              )
            })}
          </div>
        </div>

        <form
          className="overflow-auto px-6 py-5"
          onSubmit={(event) => {
            event.preventDefault()
            if (current.status === 'review') confirm.mutate()
          }}
        >
          <div className="grid grid-cols-2 gap-3">
            {isStatement && (
              <label className="col-span-2 block text-xs text-slate-500">
                {t('rev.bankAcc')}
                <select
                  className="mt-1 w-full rounded-md border border-surface-border bg-surface px-2 py-1.5 text-sm text-slate-100"
                  value={accountId}
                  onChange={(event) => setAccountId(event.target.value)}
                >
                  <option value="">{t('rev.pickAcc')}</option>
                  {bankAccounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {(isStatement ? STATEMENT_FIELDS : FIELD_DEFS).map((def) => {
              const meta = fieldMeta.get(def.key)
              const disagree = meta?.sources_agree === false
              const required = "required" in def && def.required
              return (
                <label key={def.key} className="block text-xs text-slate-500">
                  <span className="flex items-center justify-between">
                    {t(`rev.f.${def.key}`)}
                    {required ? <span className="text-pending">{t('rev.required')}</span> : null}
                    {disagree && <span className="text-loss">{t('rev.ocrClash')}</span>}
                  </span>
                  <input
                    autoFocus={def.key === 'quantity'}
                    inputMode={def.numeric ? 'decimal' : undefined}
                    className={[
                      'mt-1 w-full rounded-md border bg-surface px-2 py-1.5 text-sm text-slate-100',
                      disagree ? 'border-loss' : 'border-surface-border',
                      def.numeric ? 'num' : '',
                    ].join(' ')}
                    value={values[def.key] ?? ''}
                    onChange={(event) =>
                      setValues((prev) => ({ ...prev, [def.key]: event.target.value }))
                    }
                    onBlur={() => {
                      if (!def.numeric) return
                      setValues((prev) => ({
                        ...prev,
                        [def.key]: parseAmount(prev[def.key] ?? '') || prev[def.key] || '',
                      }))
                    }}
                  />
                </label>
              )
            })}
          </div>
        </form>
      </div>
    </div>
  )
}
